import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Pencil, Trash } from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';
import {
  createInventoryStockEntry,
  deleteInventoryStockEntry,
  getInventoryStockEntries,
  updateInventoryStockEntry
} from '../../services/api/implementation/impl';

const inr = (n) =>
  n == null || Number.isNaN(Number(n))
    ? '—'
    : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const qtyFmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
const today = () => new Date().toLocaleDateString('en-CA');

const EMPTY = { quantity: '', rate: '', entry_date: today(), note: '', adds_to_stock: true };

/**
 * Old / opening stock for one item, entered lot by lot with the rate it was
 * bought at. The weighted average (Σ qty × rate ÷ Σ qty) becomes the item's
 * average rate on the Stock page.
 */
const StockEntriesModal = ({ item, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const key = ['inventoryStockEntries', item.id];

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => getInventoryStockEntries(item.id),
    staleTime: 0
  });
  const entries = data?.data ?? [];
  const meta = data?.meta ?? {};
  const perms = meta.permissions ?? {};
  const unit = meta.unit || item.unit || '';

  const done = async (message) => {
    toast.success(message);
    setForm(EMPTY);
    setEditingId(null);
    setConfirmDeleteId(null);
    await queryClient.invalidateQueries({ queryKey: key });
    onChanged?.();
  };
  const fail = (err) => toast.error(err?.response?.data?.message || 'Could not save the stock entry');

  const save = useMutation({
    mutationFn: (payload) =>
      editingId
        ? updateInventoryStockEntry(editingId, payload)
        : createInventoryStockEntry(item.id, payload),
    onSuccess: () => done(editingId ? 'Stock entry updated' : 'Stock entry added'),
    onError: fail
  });
  const remove = useMutation({
    mutationFn: (id) => deleteInventoryStockEntry(id),
    onSuccess: () => done('Stock entry deleted'),
    onError: fail
  });

  const qty = Number(form.quantity);
  const rate = Number(form.rate);
  const valid = qty > 0 && form.rate !== '' && rate >= 0;
  const canWrite = editingId ? perms.can_edit : perms.can_add;

  // Preview of the new average before saving.
  const previewEntries = valid
    ? [...entries.filter((e) => e.id !== editingId), { quantity: qty, rate }]
    : entries;
  const previewQty = previewEntries.reduce((s, e) => s + Number(e.quantity), 0);
  const previewAvg =
    previewQty > 0
      ? previewEntries.reduce((s, e) => s + Number(e.quantity) * Number(e.rate), 0) / previewQty
      : null;

  const submit = (e) => {
    e.preventDefault();
    if (!valid || !canWrite) return;
    save.mutate({
      quantity: qty,
      rate,
      entry_date: form.entry_date || null,
      note: form.note,
      adds_to_stock: form.adds_to_stock
    });
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setConfirmDeleteId(null);
    setForm({
      quantity: String(entry.quantity),
      rate: String(entry.rate),
      entry_date: entry.entry_date || '',
      note: entry.note || '',
      adds_to_stock: entry.adds_to_stock
    });
  };

  return (
    <Modal name={`Stock & rate — ${item.item_name || 'Item'}`} width={60} onClose={onClose}>
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            { label: 'Physical stock', value: `${qtyFmt(meta.physical_stock ?? item.physicalStock)} ${unit}` },
            { label: 'Average rate', value: meta.avg_rate == null ? 'Not set' : `${inr(meta.avg_rate)} / ${unit || 'unit'}` },
            { label: 'Stock value', value: inr(meta.stock_value) },
            { label: 'Entered lots', value: `${qtyFmt(meta.total_quantity)} ${unit} · ${inr(meta.total_amount)}` }
          ].map((t) => (
            <div key={t.label} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="text-xs text-gray-500">{t.label}</div>
              <div className="mt-0.5 font-semibold text-gray-900">{t.value}</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500">
          Average rate = (quantity × rate of every entry) ÷ total quantity. Example: 600 at ₹8 + 400 at ₹6
          = ₹7.20. Stock value = physical stock × average rate.
        </p>

        {canWrite || editingId ? (
          <form onSubmit={submit} className="rounded-md border border-gray-200 p-3">
            <div className="mb-2 font-semibold text-gray-900">
              {editingId ? 'Edit stock entry' : 'Add old / opening stock'}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
              <label className="block">
                <span className="mb-1 block text-xs text-gray-500">Quantity ({unit || 'units'})</span>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  className="w-full rounded border border-gray-300 px-2 py-1.5"
                  required
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-gray-500">Rate per {unit || 'unit'} (₹)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.rate}
                  onChange={(e) => setForm({ ...form, rate: e.target.value })}
                  className="w-full rounded border border-gray-300 px-2 py-1.5"
                  required
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-gray-500">Purchased / counted on</span>
                <input
                  type="date"
                  value={form.entry_date}
                  onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
                  className="w-full rounded border border-gray-300 px-2 py-1.5"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-gray-500">Note (vendor, bill no.)</span>
                <input
                  type="text"
                  value={form.note}
                  maxLength={500}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="w-full rounded border border-gray-300 px-2 py-1.5"
                />
              </label>
            </div>
            <label className="mt-3 flex items-start gap-2 text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.adds_to_stock}
                onChange={(e) => setForm({ ...form, adds_to_stock: e.target.checked })}
              />
              <span>
                Add this quantity to physical stock
                <span className="block text-xs text-gray-500">
                  Untick if the quantity is already counted in Physical Stock and you are only recording its price.
                </span>
              </span>
            </label>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-gray-600">
                {valid ? (
                  <>
                    Amount {inr(qty * rate)} · new average{' '}
                    <span className="font-semibold">{inr(previewAvg)}</span>
                  </>
                ) : (
                  'Enter quantity and rate'
                )}
              </span>
              <div className="flex gap-2">
                {editingId ? (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(null);
                      setForm(EMPTY);
                    }}
                    className="rounded border border-gray-300 px-3 py-1.5 text-gray-700 hover:bg-gray-50">
                    Cancel
                  </button>
                ) : null}
                <button
                  type="submit"
                  disabled={!valid || !canWrite || save.isPending}
                  className="rounded bg-[#2A565E] px-3 py-1.5 font-semibold text-white disabled:opacity-50">
                  {save.isPending ? 'Saving…' : editingId ? 'Save changes' : 'Add entry'}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
            Only Inventory or Management users on this project can add stock entries.
          </p>
        )}

        <div className="max-h-[40vh] overflow-auto rounded-md border border-gray-200">
          <table className="w-full min-w-[640px] text-left">
            <thead className="sticky top-0 bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 text-right font-medium">Quantity</th>
                <th className="px-3 py-2 text-right font-medium">Rate</th>
                <th className="px-3 py-2 text-right font-medium">Amount</th>
                <th className="px-3 py-2 font-medium">Note</th>
                <th className="px-3 py-2 font-medium">Added by</th>
                <th className="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-gray-100">
                  <td className="px-3 py-2 text-gray-700">
                    {e.entry_date ? new Date(e.entry_date).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {qtyFmt(e.quantity)} {unit}
                    {!e.adds_to_stock ? (
                      <span className="ml-1 rounded bg-gray-100 px-1 text-[10px] text-gray-500" title="Price only — quantity was already in stock">
                        price only
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{inr(e.rate)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{inr(e.amount)}</td>
                  <td className="max-w-[12rem] truncate px-3 py-2 text-gray-600" title={e.note || ''}>
                    {e.note || '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{e.created_by_name || '—'}</td>
                  <td className="px-3 py-2">
                    {confirmDeleteId === e.id ? (
                      <span className="flex items-center gap-2 whitespace-nowrap text-xs">
                        <span className="text-gray-600">
                          {e.adds_to_stock ? `Remove ${qtyFmt(e.quantity)} ${unit} from stock?` : 'Delete?'}
                        </span>
                        <button
                          type="button"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(e.id)}
                          className="font-semibold text-red-700 hover:underline">
                          Delete
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(null)}
                          className="text-gray-600 hover:underline">
                          Keep
                        </button>
                      </span>
                    ) : (
                      <span className="flex items-center gap-3 text-gray-500">
                        {perms.can_edit ? (
                          <Pencil
                            className="h-4 w-4 cursor-pointer hover:text-blue-600"
                            aria-label="Edit entry"
                            onClick={() => startEdit(e)}
                          />
                        ) : null}
                        {perms.can_delete ? (
                          <Trash
                            className="h-4 w-4 cursor-pointer hover:text-red-600"
                            aria-label="Delete entry"
                            onClick={() => setConfirmDeleteId(e.id)}
                          />
                        ) : null}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {!entries.length ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                    {isLoading ? 'Loading…' : 'No stock entries yet.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
};

export default StockEntriesModal;
